import cn from 'classnames'
import React, { useState, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector, useDispatch } from 'react-redux'
import {
  ERROR_CVC_DECLINED,
  ERROR_REFUSED,
  ERROR_UNKNOWN,
  ERROR_INVALID_CARD_NUMBER,
  purchaseProduct,
  ERROR_ALREADY_PREMIUM,
  redirectUrl,
} from '../../payments'
import {
  upgradeToPremium,
  selectIsPremium,
  selectLimitedProfile,
} from '../../store/userSlice'
import { wait } from '../../utils'
import CreditCardForm, {
  AcceptedPaymentMethods,
  SecurePayments,
} from '../CreditCardForm/CreditCardForm'
import ErrorBubble from '../ErrorBubble'
import FormattedCurrency from '../FormattedCurrency'
import messages from './messages'
import { selectCoupon, selectPricingConfig } from '../../store/pricingSlice'
import { getSessionItem, setItem } from 'js/storeManager'
import Button from '../../components/Button/Button'
import { Link, useNavigate } from 'react-router-dom'
import useLocale from 'js/hooks/useLocale'
import { Scenario } from '../SignUp/SignUpPayment'
import LSButton from '../LSButton'
import LoaderThreeDots from '../Animation/LoaderThreeDots'
import { pushToDataLayer } from 'js/pages/LandingPages/utils'
import { Link as HeadLink } from 'react-head'

const mapErrorToMessage = (error) => {
  const message = {
    [ERROR_CVC_DECLINED]: messages.cvcCodeDeclined,
    [ERROR_INVALID_CARD_NUMBER]: messages.invalidCardNumber,
    [ERROR_REFUSED]: messages.cardRefused,
    [ERROR_ALREADY_PREMIUM]: messages.alreadyPremium,
    [ERROR_UNKNOWN]: messages.didntGoAsPlanned,
  }[error]

  return message || messages.didntGoAsPlanned
}

type Props = {
  amount: number
  plan: number
  againstBeige?: boolean
  oldAmount: false | number
  coupon?: string
  savedCoupon?: string
  pricingConfig: { trialDuration: number; currency: string }
  scenario: Scenario
  onLogInClick: () => void
  currency?: string
}

const Payment = (props: Props) => {
  const dispatch = useDispatch()
  const isPremium = useSelector(selectIsPremium) // (state as { user: {} }),
  const limitedProfile = useSelector(selectLimitedProfile) //(state as { user: {} }),
  const savedCoupon = useSelector(selectCoupon) //(state),
  const reduxPricing = useSelector(selectPricingConfig()) //()(state),

  const emailValueRef = useRef('')
  const [emailValue, setEmailValue] = useState('')
  const [emailValid, setEmailValid] = useState(true)
  const [errorMessage, setErrorMessage] = useState<
    { id: string; defaultMessage: string } | undefined
  >()
  const [errorIsCVC, setErrorIsCVC] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [paymentAccepted, setPaymentAccepted] = useState(false)
  const navigate = useNavigate()
  const { currency, language: web_language } = useLocale()

  function getCoupon() {
    const { coupon, scenario } = props
    if (coupon) {
      return coupon
    } else if (scenario !== 'giftcard' && savedCoupon) {
      return savedCoupon
    }
  }

  const submitPurchase = (state, dropin) => {
    const email = emailValueRef.current
    if (processing) return

    setProcessing(true)

    const { amount, scenario, plan } = props

    const allData = {
      payment_method: {
        ...state.data.paymentMethod,
        provider: 'adyen',
      },
      product: {
        type: scenario === 'giftcard' ? 'giftcard' : 'subscription',
        options: {
          duration: plan,
          currency,
          web_language,
          metadata: {
            redirect_url: redirectUrl,
            utm: {},
          },
          coupon_code: getCoupon(),
          ...(scenario === 'giftcard' ? { sender_email: email } : {}),
        },
      },
      coupon_code: null,
    }

    const utm = getSessionItem('utm')

    if (utm) {
      allData.product.options.metadata.utm = {
        ...JSON.parse(utm),
      }
    }

    if (limitedProfile) {
      allData['user_id'] = limitedProfile.id
    }

    purchaseProduct(allData)
      .then((res) => {
        const paymentData = { amount, ...allData }
        delete paymentData.payment_method

        const data = {
          scenario: props.scenario,
          email: email || null,
          responseData: res.response,
          paymentData,
          paymentProps: {
            amount: props.amount,
            isPremium,
            limitedProfile: limitedProfile,
            oldAmount: props.oldAmount,
            plan: props.plan,
            savedCoupon: props.savedCoupon,
            scenario: props.scenario,
            againstBeige: true,
          },
        }

        setItem('paymentState', JSON.stringify(data))

        if (res.response.action) {
          // 3D secure
          dropin.handleAction(res.response.action)
        } else {
          setProcessing(true)
          setPaymentAccepted(true)
          wait(1500).then(() => {
            navigate('/finalize-payment')
          })
        }
      })
      .catch((error) => {
        if (/User is already premium/.test(error)) {
          // The user account is a premium account, but for some reason it
          // is not indicated in the redux store.
          dispatch(upgradeToPremium())
        }

        setProcessing(false)
        setErrorMessage(mapErrorToMessage(error))
        setErrorIsCVC(error === ERROR_CVC_DECLINED)
        dropin.handleError()
      })

    pushToDataLayer({
      event: 'signup_form',
      section: 'select_payment',
      label: state.data.paymentMethod, //selected payment method
      success: paymentAccepted ? 1 : 0, //send 1 if the payment is successful and the user proceeds to the next step successfully, 0 if any error occurs
      error_message: errorMessage?.id, //if an error occurs when the user user clicks submit and proceeds to the next step send error messages, if no error send undefined
    })
  }

  const validateEmail = () =>
    new Promise<void>((resolve) => {
      const valid =
        /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/.test(
          emailValue,
        )

      setEmailValid(valid)

      setTimeout(resolve, 20)
    })

  const renderAmountDue = () => {
    const pricing = props.pricingConfig || reduxPricing
    const isTrial = pricing && pricing?.trialDuration > 0

    if (!props.currency && !pricing) return null

    return (
      <p className="c-pay__amount-due">
        {isTrial && props.scenario !== 'giftcard' ? (
          <span data-uid="payment_free_trial_amount">
            <FormattedMessage
              {...messages.amountDueInDays}
              values={{ days: pricing.trialDuration }}
            />
          </span>
        ) : (
          <FormattedMessage {...messages.amountDue} />
        )}
        {': '}
        {props.oldAmount && (
          <>
            <del data-uid="payment_original_amount">
              <FormattedCurrency
                currency={props.currency || pricing.currency}
                value={props.oldAmount}
              />
            </del>{' '}
            <ins data-uid="payment_discounted_amount">
              <FormattedCurrency
                currency={props.currency || pricing.currency}
                value={props.amount}
              />
            </ins>
          </>
        )}
        {!props.oldAmount && (
          <FormattedCurrency
            currency={props.currency || pricing.currency}
            value={props.amount}
          />
        )}
      </p>
    )
  }

  const handleEmailChange = (e) => {
    const { value } = e.target
    setEmailValue(value)
    emailValueRef.current = value
  }

  const handleEmailBlur = () => {
    validateEmail()
  }

  const handleBeforeSubmit = () => validateEmail().then(() => emailValid)

  const { scenario, againstBeige, onLogInClick } = props

  const pricing = props.pricingConfig || reduxPricing
  const isTrial = pricing && pricing.trialDuration !== 0

  return (
    <>
      {/* Base css for adyen web-dropin, get versions from https://docs.adyen.com/online-payments/release-notes/?integration_type=web (click Embed script tab) */}
      <HeadLink
        rel="stylesheet"
        href="https://checkoutshopper-live.adyen.com/checkoutshopper/sdk/5.57.0/adyen.css"
        integrity="sha384-zgFNrGzbwuX5qJLys75cOUIGru/BoEzhGMyC07I3OSdHqXuhUfoDPVG03G+61oF4"
        crossOrigin="anonymous"
      />

      <div
        className={cn('c-pay c-pay--light', {
          'c-pay--against-beige': againstBeige,
        })}
      >
        {isPremium && scenario !== 'giftcard' && (
          <div>
            <p className="mb-12" data-uid="error-already-premium">
              <FormattedMessage {...messages.alreadyPremium} />
            </p>
            <Button
              use={Link}
              to="/account/subscription"
              data-uid="manage-subscription-button"
            >
              <FormattedMessage {...messages.manageSubscription} />
            </Button>
          </div>
        )}
        {(!isPremium || scenario === 'giftcard') && (
          <>
            {scenario !== 'giftcard' &&
              limitedProfile &&
              limitedProfile.email && (
                <div
                  className="c-upgrade__account-info mx-48 mb-32 bg-BgInfo leading-default"
                  data-uid="account-info"
                >
                  <p>
                    <FormattedMessage
                      {...messages['upgrade.connected.info']}
                      values={{
                        email: (
                          <strong className="s-text">
                            {limitedProfile.email}
                          </strong>
                        ),
                      }}
                    />{' '}
                    <button onClick={onLogInClick}>
                      <FormattedMessage {...messages['SignUp.logInHere']} />
                    </button>
                  </p>
                </div>
              )}
            <CreditCardForm
              giftcard={scenario === 'giftcard' || false}
              onBeforeSubmit={
                scenario === 'giftcard' ? handleBeforeSubmit : undefined
              }
              onSubmit={submitPurchase}
              emailValid={emailValid}
            >
              {({ formProps, payPalSelected, adyenCheckout }) => (
                <>
                  <form {...formProps} noValidate>
                    <AcceptedPaymentMethods />
                    {errorMessage && (
                      <div
                        className="c-pay__error"
                        data-uid={errorIsCVC ? 'error-cvc' : 'error'}
                      >
                        <p className="mb-12">
                          <FormattedMessage {...errorMessage} />
                        </p>
                        <p>
                          <FormattedMessage
                            {...messages.contactCustomerSupport}
                            values={{
                              a: function Link(...chunks) {
                                return (
                                  <a href="https://lifesum.com/contact">
                                    {chunks}
                                  </a>
                                )
                              },
                            }}
                          />
                        </p>
                      </div>
                    )}
                    {scenario === 'giftcard' && (
                      <div className="c-pay__fields-wrapper">
                        <div
                          className={cn('c-pay__field-wrapper', {
                            'c-pay__field-wrapper--invalid': !emailValid,
                          })}
                        >
                          <input
                            value={emailValue}
                            onChange={handleEmailChange}
                            onBlur={handleEmailBlur}
                            className={cn('c-pay__field c-pay__field--email', {
                              'c-pay__field--has-value': emailValue !== '',
                              'c-pay__field--invalid': !emailValid,
                            })}
                            id="cc-email"
                            autoComplete="cc-email"
                            type="email"
                            data-uid="payment_email_field"
                          />
                          <label className="c-pay__label" htmlFor="cc-email">
                            <FormattedMessage {...messages['your_email']} />
                          </label>
                          {!emailValid && (
                            <div className="c-pay__error-bubble">
                              <ErrorBubble>
                                Are you sure you entered your email address
                                correctly?
                              </ErrorBubble>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {adyenCheckout}
                    {payPalSelected && (
                      <p className="mt-24">
                        <FormattedMessage {...messages['pay_paypal_text']} />
                      </p>
                    )}
                    {isTrial && scenario !== 'giftcard' && (
                      <p
                        className="mt-24 leading-body type-14 lg:type-17"
                        data-uid="payment_free_trial_info"
                      >
                        <FormattedMessage
                          {...messages['pay.free_trial_info']}
                        />
                      </p>
                    )}
                    <LSButton
                      type="submit"
                      data-uid="submit_button"
                      className="w-full"
                      variant="primary"
                      size="large"
                      upperCase
                      link={false}
                      disabled={processing}
                    >
                      <span>
                        {payPalSelected ? (
                          <FormattedMessage
                            {...messages['pay_continue_to_paypal']}
                          />
                        ) : scenario === 'giftcard' ? (
                          <FormattedMessage {...messages['pay_btn_gift']} />
                        ) : isTrial ? (
                          <FormattedMessage
                            {...messages['pay.free_trial_btn']}
                          />
                        ) : (
                          <FormattedMessage {...messages['pay.btn']} />
                        )}
                      </span>
                    </LSButton>
                  </form>
                  {processing && (
                    <div
                      className="absolute z-30"
                      style={{ top: -10, right: -10, bottom: -10, left: -10 }}
                    >
                      <div
                        className={cn(
                          'absolute left-1/2 top-1/2 w-1/2 flex-col items-center rounded-8 bg-BgContent px-12 py-24 shadow-subtle',
                        )}
                        style={{
                          transform: 'translateX(-50%) translateY(-50%)',
                        }}
                      >
                        {!paymentAccepted ? (
                          <div className="flex flex-col items-center">
                            <LoaderThreeDots />
                            <p className="quincy text-32">
                              {payPalSelected ? (
                                <FormattedMessage
                                  id="Redeem.pay.paypalRedirect"
                                  defaultMessage="Redirecting you to PayPal"
                                />
                              ) : (
                                <FormattedMessage
                                  {...messages.processingPayment}
                                />
                              )}
                            </p>
                          </div>
                        ) : (
                          <div>
                            <>
                              <img
                                src={
                                  'https://www.datocms-assets.com/23496/1701618145-throphy.png'
                                }
                                alt=""
                              />
                              <p className="quincy text-32">
                                <FormattedMessage
                                  {...messages.paymentAccepted}
                                />
                              </p>
                            </>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </CreditCardForm>
            <div className="c-pay__info">
              <SecurePayments />
              {renderAmountDue()}
              {/* <p className="leading-body mb-8">
                  <FormattedMessage {...messages['SignUp.SubWillRenew']} />
                </p> */}
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default Payment
